// scenes 16–20
import { motion } from 'framer-motion';
import { ScanFace, AudioLines, Link2, Check, Sparkles, FileText, UserRound, Film, Volume2, VolumeX } from 'lucide-react';
import { useSceneTime, seg, EASE } from '../engine/sceneTime';
import { Words, Fade, Eyebrow, Line } from '../components/Kinetic';
import { Vid, PhoneFrame } from '../components/Media';
import { IMAGES, VIDEOS } from '../assets';

// ── 16 BETTER EDITING — BEAT MAPPING ────────────────────────────
const BARS = Array.from({ length: 150 }, (_, i) => 18 + Math.abs(Math.sin(i * 0.9) * 54 + Math.sin(i * 0.23) * 26) + (i % 19 === 0 ? 40 : 0));
const CLIP_POSTERS = [VIDEOS.propertyOpen, VIDEOS.jsSneakPeek, VIDEOS.jsJustListed, VIDEOS.presentourExample, VIDEOS.propertyTour, VIDEOS.jsSold, VIDEOS.narratourExample, VIDEOS.propertyAI];
const OFFSETS = [38, -52, 61, -44, 72, -36, 49, -58];

export function Beats({ scene }) {
  const { t } = useSceneTime();
  const W = 1240; const beats = 8; const step = W / beats;
  const analysing = t >= 7 && t < 9.5;
  const compact = t >= 14;
  return (
    <div className="pk-fill">
      <div className="pk-abs" style={{ left: 120, top: 120, width: 1200 }}>
        <Eyebrow show={t >= 0.3}>Better editing</Eyebrow>
        <Words text={scene.headline} show={t >= 0.6} size={104} lime={[1]} style={{ marginTop: 14 }} />
      </div>
      <motion.div className="pk-abs pk-glass pk-timeline" style={{ left: 120, top: 420, width: W + 80 }} initial={false}
        animate={{ opacity: t >= 1 ? 1 : 0, y: t >= 1 ? 0 : 60, x: compact ? -20 : 0, scale: compact ? 0.92 : 1 }} transition={{ duration: 0.9, ease: EASE }}>
        <div className="pk-tl-head">
          <span>Track · 124 BPM</span>
          <motion.span className="pk-tl-status" initial={false} animate={{ opacity: analysing ? 1 : t >= 9.5 ? 1 : 0 }}>{t >= 9.5 ? 'Beats mapped' : 'AI analysing track…'}</motion.span>
        </div>
        <div className="pk-tl-wave" style={{ width: W }}>
          {BARS.map((h, i) => (
            <span key={i} style={{ height: h, animationDelay: `${(i % 12) * 0.08}s`, opacity: t >= 9.5 && Math.floor(i / (150 / beats)) % 2 === 0 ? 1 : 0.55 }} />
          ))}
          <motion.div className="pk-tl-scan" initial={false} animate={{ left: analysing ? W : 0, opacity: analysing ? 1 : 0 }} transition={{ duration: 2.4, ease: 'linear' }} />
          {Array.from({ length: beats }, (_, i) => {
            const lit = t >= 9.3 + i * 0.28;
            return <motion.div key={i} className="pk-tl-beat" style={{ left: i * step }} initial={false} animate={{ opacity: t >= 2.5 ? 1 : 0, backgroundColor: lit ? '#C6EE5A' : 'rgba(255,255,255,0.28)', boxShadow: lit ? '0 0 18px rgba(150,193,31,0.9)' : '0 0 0 rgba(0,0,0,0)' }} transition={{ duration: 0.3 }} />;
          })}
        </div>
        <div className="pk-tl-clips" style={{ width: W }}>
          {CLIP_POSTERS.map((v, i) => {
            const shown = t >= 3 + i * 0.3;
            const snapped = t >= 11 + i * 0.3;
            return (
              <motion.div key={i} className={`pk-tl-clip ${snapped ? 'snapped' : ''}`} style={{ width: step - 10 }} initial={false}
                animate={{ opacity: shown ? 1 : 0, left: i * step + (snapped ? 0 : OFFSETS[i]), y: shown ? 0 : 20 }}
                transition={snapped ? { type: 'spring', stiffness: 420, damping: 24 } : { duration: 0.6, ease: EASE }}>
                <img src={v.poster} alt="" />
                <span>{i + 1}</span>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
      <motion.div className="pk-abs" style={{ left: 1500, top: 330 }} initial={false}
        animate={{ opacity: compact ? 1 : 0, x: compact ? 0 : 120, scale: compact ? 1 : 0.9 }} transition={{ duration: 0.9, ease: EASE }}>
        <PhoneFrame width={290} glow label="Final reel">
          <Vid video={scene.media.reel} className="pk-fill" active={compact} />
        </PhoneFrame>
      </motion.div>
      <Fade show={t >= 16} y={12} className="pk-sub" style={{ position: 'absolute', left: 120, top: 960, fontSize: 40 }}>
        <span className="pk-lime">Faster edits.</span> Stronger finishes.
      </Fade>
    </div>
  );
}

// ── 17 THE PROPERTY TOUR, REINVENTED ────────────────────────────
// Face mesh sits over the agent's head (portrait framed chest-up: face centre ≈ 50% × 31%).
const FW = 320; const FH = 569;
const MESH = Array.from({ length: 36 }, (_, i) => {
  const a = (i / 36) * Math.PI * 2; const r = 0.5 + ((i * 7) % 6) * 0.1;
  return { x: 160 + Math.cos(a) * 55 * r, y: 176 + Math.sin(a) * 75 * r };
});
const MESH_LINKS = MESH.map((_, i) => [i, (i + 1) % MESH.length]).concat(MESH.map((_, i) => [i, (i + 7) % MESH.length]));
const BEAM_DOTS = Array.from({ length: 9 }, (_, i) => ({ top: 320 + (i % 3) * 14, delay: i * 0.13 }));

function Mesh({ beamY, ghost = false }) {
  return (
    <svg className={`pk-abs pk-mesh ${ghost ? 'pk-mesh-ghost' : ''}`} width={FW} height={FH} viewBox={`0 0 ${FW} ${FH}`} aria-hidden>
      {MESH_LINKS.map(([a, b], i) => (
        <line key={i} x1={MESH[a].x} y1={MESH[a].y} x2={MESH[b].x} y2={MESH[b].y} className={ghost || beamY >= Math.max(MESH[a].y, MESH[b].y) ? 'on' : ''} />
      ))}
      {MESH.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={2.4} className={ghost || beamY >= p.y ? 'on' : ''} />)}
    </svg>
  );
}

function Corners() {
  return <><div className="pk-ai-corner tl" /><div className="pk-ai-corner tr" /><div className="pk-ai-corner bl" /><div className="pk-ai-corner br" /></>;
}

function AgentMedia({ scene, active, className = '' }) {
  const img = scene.media.agentImage?.url;
  if (img) return <img src={img} alt="" className={`pk-fill pk-agent-img ${className}`} />;
  return <Vid video={scene.media.agent} className={`pk-fill ${className}`} active={active} />;
}

export function Tours({ scene }) {
  const { t } = useSceneTime();
  const scanP = seg(t, 2.5, 6.5);
  const scan = t >= 2.5 && t < 6.5;
  const twin = t >= 6.5;
  const transfer = twin && t < 8.2;
  const voice = t >= 8;
  const multiply = t >= 13;
  const beamY = scanP * FH;
  const framesIn = t >= 1.2;
  return (
    <div className="pk-fill">
      <div className="pk-abs" style={{ left: 120, top: 160, width: 900 }}>
        <Eyebrow show={t >= 0.4}>The property tour, reinvented</Eyebrow>
        {scene.products.map((p, i) => {
          const on = t >= 1 + i * 4;
          return (
            <motion.div key={p.a} className="pk-tour-name" initial={false} animate={{ opacity: on ? 1 : 0, x: on ? 0 : -60 }} transition={{ duration: 0.9, ease: EASE }} style={{ marginTop: i === 0 ? 20 : 30 }} data-testid={`pk-tour-${i}`}>
              <div className="pk-tour-word"><span className="pk-tour-a">{p.a}</span><span className="pk-tour-b">{p.b}</span></div>
              <div className="pk-tour-sub">{p.sub}</div>
            </motion.div>
          );
        })}
        <Line show={t >= 9} width={420} style={{ marginTop: 36 }} />
        <div style={{ marginTop: 22 }}>
          <Words text={scene.statement[0]} show={t >= 9.3} size={68} className="pk-light" />
          <Words text={scene.statement[1]} show={t >= 10.2} size={68} lime={[0, 1]} />
        </div>
      </div>
      <div className="pk-abs pk-studio" style={{ left: 1080, top: 150, width: 760, height: 790 }} data-testid="pk-studio">
        <div className="pk-studio-grid" />
        <div className="pk-studio-floor" />

        <motion.div className="pk-abs pk-capture" style={{ left: 40, top: 60, width: FW, height: FH }} initial={false}
          animate={{ opacity: framesIn ? (multiply ? 0 : 1) : 0, scale: framesIn ? (multiply ? 0.8 : 1) : 0.92, y: multiply ? 60 : 0 }} transition={{ duration: 0.9, ease: EASE }} data-testid="pk-capture-agent">
          <AgentMedia scene={scene} active={framesIn && !multiply} />
          <Mesh beamY={beamY} />
          <motion.div className="pk-scan-beam" initial={false} animate={{ top: beamY, opacity: scan ? 1 : 0 }} transition={{ top: { duration: 0.1, ease: 'linear' }, opacity: { duration: 0.4 } }} />
          <Corners />
          <div className="pk-capture-tag"><ScanFace size={16} /> Recorded agent</div>
          <div className="pk-capture-pct">{scan || twin ? `${Math.round(scanP * 100)}%` : 'REC ●'}</div>
          <div className="pk-capture-readouts">
            {scene.readouts.map((r, i) => {
              const on = t >= 3.4 + i * 1.05;
              return <motion.span key={r} initial={false} animate={{ opacity: on ? 1 : 0, x: on ? 0 : -12 }} transition={{ duration: 0.5, ease: EASE }}><Check size={12} /> {r}</motion.span>;
            })}
          </div>
        </motion.div>

        {BEAM_DOTS.map((d, i) => (
          <motion.span key={i} className="pk-beam-dot" style={{ top: d.top }} initial={false}
            animate={transfer ? { left: [360, 400], opacity: [0, 1, 0] } : { left: 360, opacity: 0 }}
            transition={transfer ? { duration: 0.75, delay: d.delay, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }} />
        ))}

        <motion.div className={`pk-abs pk-capture pk-clone ${twin ? 'live' : ''}`} style={{ left: 400, top: 60, width: FW, height: FH }} initial={false}
          animate={{ opacity: framesIn ? (multiply ? 0 : 1) : 0, scale: framesIn ? (multiply ? 0.8 : 1) : 0.92, y: multiply ? 60 : 0 }} transition={{ duration: 0.9, ease: EASE }} data-testid="pk-capture-clone">
          <motion.div className="pk-fill pk-clone-body" initial={false} animate={{ opacity: twin ? 1 : 0 }} transition={{ duration: 1.4, ease: EASE }}>
            <AgentMedia scene={scene} active={twin && !multiply} className="pk-clone-vid" />
            <div className="pk-clone-tint" />
            <div className="pk-clone-scanlines" />
          </motion.div>
          <motion.div className="pk-fill" initial={false} animate={{ opacity: twin ? 0.35 : 0.9 }} transition={{ duration: 1.4 }}><Mesh beamY={0} ghost /></motion.div>
          <Corners />
          <div className="pk-capture-tag lime"><Sparkles size={16} /> Digital presenter</div>
          <div className={`pk-capture-pct ${twin ? 'lime' : ''}`}>{twin ? 'CLONE READY' : 'AWAITING'}</div>
        </motion.div>

        <motion.div className="pk-studio-voice" style={{ left: 40, top: 668, width: 680 }} initial={false}
          animate={{ opacity: voice && !multiply ? 1 : 0, y: voice ? 0 : 20 }} transition={{ duration: 0.8, ease: EASE }}>
          <AudioLines size={22} />
          <div className="pk-voice-bars">{Array.from({ length: 48 }, (_, i) => <span key={i} style={{ animationDelay: `${(i % 9) * 0.1}s`, animationPlayState: voice ? 'running' : 'paused' }} />)}</div>
          <span className="pk-voice-tag">Voice cloned</span>
        </motion.div>

        {scene.media.tiles.map((v, i) => (
          <motion.div key={v.label} className="pk-abs" style={{ left: 40 + i * 235, top: 170 }} initial={false}
            animate={{ opacity: multiply ? 1 : 0, y: multiply ? 0 : 90, scale: multiply ? 1 : 0.8 }} transition={{ duration: 0.9, delay: multiply ? i * 0.25 : 0, ease: EASE }}>
            <PhoneFrame width={190} glow={i === 0}>
              <Vid video={v} className="pk-fill" active={multiply} />
            </PhoneFrame>
            <div className="pk-platform">{['Property 1', 'Property 2', 'Property 3'][i]}</div>
          </motion.div>
        ))}
        <Fade show={multiply} delay={0.9} y={10} className="pk-caption" style={{ position: 'absolute', left: 40, top: 616 }}>One capture → every property</Fade>
      </div>
    </div>
  );
}

// ── 18 FROM RIGHTMOVE LINK TO BESPOKE SCRIPT ────────────────────
function Typed({ text, progress }) {
  const n = Math.floor(Math.max(0, Math.min(1, progress)) * text.length);
  return <>{text.slice(0, n)}{n < text.length && <span className="pk-caret" />}</>;
}

export function Script({ scene }) {
  const { t } = useSceneTime();
  const step = t < 4 ? 1 : t < 6 ? 2 : t < 9.5 ? 3 : 4;
  return (
    <div className="pk-fill">
      <div className="pk-abs" style={{ left: 120, top: 100, width: 1680 }}>
        <Eyebrow show={t >= 0.3}>Script Maker · from link to bespoke script</Eyebrow>
        <Words text={scene.headline} show={t >= 0.6} size={88} lime={[1]} style={{ marginTop: 12 }} />
      </div>
      <motion.div className="pk-abs pk-glass pk-window" style={{ left: 170, top: 330, width: 1580, height: 640 }} initial={false}
        animate={{ opacity: t >= 1 ? 1 : 0, y: t >= 1 ? 0 : 50 }} transition={{ duration: 0.9, ease: EASE }} data-testid="pk-script-window">
        <div className="pk-window-bar">
          <span className="pk-dots"><i /><i /><i /></span>
          <div className="pk-steps">
            {['Paste', 'Read', 'Rooms', 'Script'].map((s, i) => (
              <span key={s} className={`pk-step ${step === i + 1 ? 'on' : step > i + 1 ? 'done' : ''}`}><b>{i + 1}</b>{s}{step > i + 1 && <Check size={14} />}</span>
            ))}
          </div>
        </div>
        <div className="pk-window-body">
          <div className="pk-window-left">
            <div className="pk-url">
              <Link2 size={20} />
              <span><Typed text={scene.url} progress={seg(t, 1.3, 3.6)} /></span>
            </div>
            <motion.div className="pk-read" initial={false} animate={{ opacity: t >= 4 ? 1 : 0 }}>
              <span>AI reads property information</span>
              <div className="pk-read-bar"><motion.div initial={false} animate={{ width: `${seg(t, 4, 6) * 100}%` }} transition={{ duration: 0.2 }} /></div>
              <small>Features · description · photos · location</small>
            </motion.div>
            <div className="pk-rooms">
              {scene.rooms.map((r, i) => {
                const on = t >= 6.2 + i * 0.6;
                const lineP = seg(t, 9.6 + i * 1.3, 10.9 + i * 1.3);
                return (
                  <motion.div key={r} className={`pk-room ${lineP > 0 ? 'active' : ''}`} initial={false} animate={{ opacity: on ? 1 : 0, x: on ? 0 : -30 }} transition={{ duration: 0.6, ease: EASE }}>
                    <div className="pk-room-name">{r}</div>
                    <div className="pk-room-line">{lineP > 0 && <Typed text={scene.lines[r]} progress={lineP} />}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
          <div className="pk-window-right">
            <motion.div className="pk-inputs" initial={false} animate={{ opacity: t >= 9.2 ? 1 : 0 }}>
              <div className="pk-inputs-head"><Sparkles size={18} /> Script generated using</div>
              {scene.inputs.map((inp, i) => (
                <motion.span key={inp} className="pk-input-chip" initial={false} animate={{ opacity: t >= 9.4 + i * 0.5 ? 1 : 0, y: t >= 9.4 + i * 0.5 ? 0 : 10 }}>{inp}</motion.span>
              ))}
            </motion.div>
            <Fade show={t >= 16} y={10} className="pk-window-note">Written from the property's own information. Never invented.</Fade>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── 19 JACKSON-STOPS EXAMPLES ───────────────────────────────────
const STEP_ICONS = [FileText, UserRound, Film];
export function JacksonStops({ scene }) {
  const { t } = useSceneTime();
  const start = scene.examplesAt ?? 4; const each = scene.eachSeconds ?? 8;
  const pipeline = t < start;
  // Sound follows one phone at a time: Mark first, then Charley takes over.
  const lead = pipeline ? -1 : Math.min(scene.presenters.length - 1, Math.floor((t - start) / each));
  const slots = [{ left: 540 }, { left: 1000 }];
  return (
    <div className="pk-fill">
      <motion.div className="pk-abs" style={{ left: 0, width: 1920, textAlign: 'center' }} initial={false}
        animate={{ top: pipeline ? 250 : 56 }} transition={{ duration: 0.9, ease: EASE }}>
        <motion.div initial={false} animate={{ scale: pipeline ? 1 : 0.64 }} transition={{ duration: 0.9, ease: EASE }} style={{ transformOrigin: 'top center' }}>
          <Words text={scene.headline} show={t >= 0.3} size={92} className="pk-words-center" lime={[4, 5]} stagger={0.06} />
        </motion.div>
      </motion.div>
      <Fade show={t >= 0.9 && pipeline} y={20} className="pk-abs pk-render" style={{ left: 0, top: 470, width: 1920 }}>
        {scene.steps.map((label, i) => {
          const Icon = STEP_ICONS[i] || Check;
          const done = t >= 1.3 + i * 0.85;
          return (
            <div key={label} className="pk-render-step">
              {i > 0 && <motion.div className="pk-render-link" initial={false} animate={{ scaleX: done ? 1 : 0 }} transition={{ duration: 0.6, ease: EASE }} />}
              <motion.div className={`pk-glass pk-render-card ${done ? 'pk-glass-active' : ''}`} initial={false}
                animate={{ scale: done ? 1 : 0.92, opacity: done ? 1 : 0.45 }} transition={{ duration: 0.5, ease: EASE }}>
                <span className="pk-render-icon"><Icon size={34} /></span>
                <span className="pk-render-label">{label}</span>
                <motion.span className="pk-render-tick" initial={false} animate={{ scale: done ? 1 : 0 }} transition={{ type: 'spring', stiffness: 260, damping: 18 }}><Check size={18} /></motion.span>
              </motion.div>
            </div>
          );
        })}
      </Fade>
      {scene.presenters.map((p, i) => {
        const shown = !pipeline;
        const from = start + i * each; const to = from + each;
        const started = t >= from;
        const live = lead === i;
        // Sound ramps up over 0.4s and fades out over the last 1.2s of each presenter's window.
        const vol = Math.min(1, Math.max(0, (t - from) / 0.4), 1) * Math.min(1, Math.max(0, (to - t) / 1.2));
        return (
          <motion.div key={p.name} className="pk-abs" style={{ left: slots[i].left, top: 170 }} initial={false}
            animate={{ opacity: shown ? (live ? 1 : 0.55) : 0, y: shown ? 0 : 60, scale: shown ? (live ? 1 : 0.94) : 0.9 }}
            transition={{ duration: 0.9, ease: EASE }} data-testid={`pk-presenter-${i}`}>
            <PhoneFrame width={380} glow={live}>
              <Vid video={p.video} className="pk-fill" active={shown && started} restart silent={!live} volume={live ? vol : 0} />
            </PhoneFrame>
            <div className={`pk-platform pk-presenter-name ${live ? 'pk-live' : ''}`}>
              {live ? <Volume2 size={16} /> : <VolumeX size={16} />} {p.name} · PresenTour
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

// ── 20 THE FINALE ───────────────────────────────────────────────
// Mirrors the NicheSocial homepage intro: two value beats fade through while the product roundabout flies in behind, faded.
const RING_R = 500;
function Beat({ show, children }) {
  return (
    <motion.div className="pk-abs pk-beat" style={{ left: 0, top: 0, width: 1920, height: 1080 }} initial={false}
      animate={{ opacity: show ? 1 : 0, scale: show ? 1 : 0.985 }} transition={{ duration: 1, ease: 'easeInOut' }}>
      <h2>{children}</h2>
    </motion.div>
  );
}

export function Finale({ scene }) {
  const { t } = useSceneTime();
  const cards = scene.wall; const n = cards.length;
  const beat1 = t >= 0.6 && t < 3.9; const beat2 = t >= 3.9 && t < 7.4;
  const front = t >= 7.4; const title = t >= 10.5;
  const ring = !front
    ? { scale: t >= 0.6 ? 0.6 : 0.2, opacity: t >= 0.6 ? 0.32 : 0, y: 0 }
    : title ? { scale: 0.7, opacity: 0.22, y: 250 } : { scale: 1, opacity: 1, y: 30 };
  return (
    <div className="pk-fill">
      <motion.div className="pk-abs pk-ring-wrap" style={{ left: 0, top: 0, width: 1920, height: 1080 }} initial={false}
        animate={ring} transition={{ duration: front ? 2.4 : 6.5, ease: EASE }}>
        <div className="pk-ring" style={{ animationPlayState: t >= 0.2 ? 'running' : 'paused' }}>
          {cards.map((item, i) => (
            <div key={`${item.label}-${i}`} className="pk-ring-card" style={{ transform: `rotateY(${(360 / n) * i}deg) translateZ(${RING_R}px)` }}>
              <img src={item.v.poster} alt="" />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </motion.div>
      {scene.beats.map(([plain, lime], i) => (
        <Beat key={lime} show={i === 0 ? beat1 : beat2}>{plain}<em>{lime}</em></Beat>
      ))}
      <motion.div className="pk-beat-bar" initial={false} animate={{ opacity: t >= 0.6 && t < 7.4 ? 1 : 0 }} transition={{ duration: 0.5 }}>
        <motion.span initial={false} animate={{ scaleX: t >= 0.6 ? 1 : 0 }} transition={{ duration: 6.8, ease: 'linear' }} />
      </motion.div>
      <div className="pk-abs" style={{ left: 0, top: 230, width: 1920, textAlign: 'center' }}>
        <motion.div className="pk-product-word" style={{ fontSize: 120 }} initial={false}
          animate={{ opacity: title ? 1 : 0, letterSpacing: title ? '0.01em' : '0.3em', filter: title ? 'blur(0px)' : 'blur(14px)' }} transition={{ duration: 1.3, ease: EASE }}>
          SOCIAL <span className="pk-lime">PRO</span>
        </motion.div>
        <div style={{ marginTop: 40 }}>
          <Words text={scene.headline} show={t >= 12.4} size={64} className="pk-words-center pk-light" stagger={0.09} nowrap />
        </div>
        <Fade show={t >= 14.8} y={14} style={{ marginTop: 70, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
          <div className="pk-x-row">
            <img src={IMAGES.nichecomLogo.url} alt="NicheCom" style={{ height: 96 }} />
            <span className="pk-x">×</span>
            <img src={IMAGES.jacksonStopsLogo.url} alt="Jackson-Stops" className="pk-js-logo" style={{ height: 150 }} />
          </div>
          <div className="pk-caption">{scene.small}</div>
        </Fade>
      </div>
    </div>
  );
}
